import {closeCurrentDialog, closeDialog, openDialog} from 'platform/components';
import {match} from 'ts-pattern';

import {isEmpty, isNil, isNotNil} from 'ramda';
import {isArray} from 'ramda-adjunct';

import i18n from '@dms/i18n';
import {catchUnhandledDataGridActions, openVehicleHistoryServiceRequest} from '@dms/shared';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import type {ActionCallback} from 'features/eag-grid';

import {CopyHistoryItem} from '../components/CopyHistoryItem';
import {CopyRequest} from '../components/CopyRequest';
import {parseVehicleHistoryJobRow} from '../utils/parseVehicleHistoryJobRow';
import {refreshServiceOrderGrids} from '../utils/refreshServiceOrderGrids';

interface UseVehicleHistoryActionCallbacksProps extends RequiredTestIdProps {
  serviceCaseId: string;
}

const COPY_REQUEST_DIALOG_ID = 'copyJobDialog';

/**
 * Datagrid actions for the vehicle service history, shared by the History of repairs section and
 * the Vehicle history dialog in the header. Copies always target an order of the current service
 * case, so both entry points need the same serviceCaseId.
 */
export function useVehicleHistoryActionCallbacks(props: UseVehicleHistoryActionCallbacksProps) {
  const serviceRequestsActionCallback: ActionCallback = ({actionKey, rowData}) => {
    const rows = (isArray(rowData) ? rowData : [rowData])
      .map(parseVehicleHistoryJobRow)
      .filter(isNotNil);

    match(actionKey)
      .with('copy', () => {
        if (isEmpty(rows)) {
          return;
        }

        openDialog(
          <CopyRequest
            serviceCaseId={props.serviceCaseId}
            items={rows.map((row) => ({
              serviceJobId: row.id,
              serviceOrderId: row.serviceOrderId ?? '',
            }))}
            onCopied={refreshServiceOrderGrids}
            onClose={() => closeDialog(COPY_REQUEST_DIALOG_ID)}
            data-testid={suffixTestId('copyJobDialog', props)}
          />,
          {
            id: COPY_REQUEST_DIALOG_ID,
            title: i18n.t('entity.order.labels.copyToServiceOrder'),
          }
        );
      })
      .with('open', () => {
        const [row] = rows;

        if (isArray(rowData) || isNil(row)) {
          return;
        }

        openVehicleHistoryServiceRequest({
          serviceCaseId: row.serviceCaseId,
          serviceOrderId: row.serviceOrderId,
          serviceJobId: row.id,
        });
      })
      .otherwise(() => catchUnhandledDataGridActions(actionKey));
  };

  const workItemsActionCallback: ActionCallback = ({actionKey, rowId}) =>
    match(actionKey)
      .with('copy', () =>
        openDialog(
          <CopyHistoryItem
            serviceCaseId={props.serviceCaseId}
            itemIds={isArray(rowId) ? rowId : [rowId]}
            onCopied={refreshServiceOrderGrids}
            onClose={closeCurrentDialog}
            data-testid={suffixTestId('copyDialog', props)}
          />,
          {
            title: i18n.t('entity.order.labels.copyToServiceOrder'),
          }
        )
      )
      .otherwise(() => catchUnhandledDataGridActions(actionKey));

  return {serviceRequestsActionCallback, workItemsActionCallback};
}
